// In ProductCarouselThumbnail.tsx
import React, { ComponentProps, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { Box, styled, useMediaQuery, useTheme } from '@mui/material'

import { ProductCarouselContext, useProductCarouselContext } from './ProductCarouselContext'

export type ProductCarouselThumbnailProps = Omit<ComponentProps<typeof Box>, 'sx'> & {
  children: React.ReactNode
  index: number
}

export const ProductCarouselThumbnail: React.FC<ProductCarouselThumbnailProps> = ({ children, index, ...props }) => {
  const ref = useRef<HTMLDivElement>(null)
  const { flickityRef, isFlickityActive } = useContext(ProductCarouselContext)
  const [active, setActive] = useState(index === 0)
  const { thumblistRef } = useProductCarouselContext()
  const theme = useTheme()
  const isMobile = !useMediaQuery(theme.breakpoints.up('sm'))

  useEffect(() => {
    if (flickityRef && flickityRef.current) {
      const refElem = flickityRef.current
      const onSlideChange = (selectedIndex: number) => {
        setActive(index === selectedIndex)

        if (ref && ref.current && thumblistRef && thumblistRef.current && selectedIndex === index) {
          if (isMobile) {
            // Horizontal scrolling for mobile
            const leftPosition = ref.current.offsetLeft

            thumblistRef.current.scrollLeft = leftPosition - 80
          } else {
            // Vertical scrolling for desktop
            const topPosition = ref.current.offsetTop

            thumblistRef.current.scrollTop = topPosition
          }
        }
      }

      refElem.on('change', onSlideChange)

      return () => {
        refElem.off('change', onSlideChange)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFlickityActive, ref, flickityRef, index, isMobile])

  // Change the current slide
  const onClick = useCallback(() => {
    if (flickityRef && flickityRef.current) {
      flickityRef.current.select(index)
    }
  }, [index, flickityRef])

  return (
    <StyledBox 
      ref={ref} 
      onClick={onClick} 
      $active={active} 
      $isMobile={isMobile}
      {...props}
    >
      {children}
    </StyledBox>
  )
}

export const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$active' && prop !== '$isMobile',
}) <{ $active?: boolean; $isMobile?: boolean }>`
  position: relative;
  cursor: pointer;
  overflow: hidden;
  
  width: ${pxToRem(60)};
  height: ${pxToRem(60)};
  min-width: ${pxToRem(60)};
  border-radius: ${pxToRem(8)};
  
  &:before {
    display: block;
    content: "";
    position: absolute;
    left: 0; right: 0;
    top: 0; bottom: 0;
    background: rgba(255,255,255,0.5);
    z-index: 1;
    border-radius: ${pxToRem(8)};
  }
  
  ${props => props.$active && '&:before { display: none; }'}
  
  ${props => props.$active && `
    box-shadow: 0 ${props.theme.typography.pxToRem(2)} ${props.theme.typography.pxToRem(4)} ${props.theme.palette.custom.pink};
  `}

  ${props => props.theme.breakpoints.up('sm')} {
    width: ${pxToRem(60)};
    height: ${pxToRem(60)};
    border-radius: ${pxToRem(12)};
    margin-left: auto !important;
    margin-right: auto !important;

    &:hover:before {
      display: none;
    }

    &:before {
      display: block;
      content: "";
      position: absolute;
      left: 0; right: 0;
      top: 0; bottom: 0;
      background: rgba(255,255,255,0.5);
      z-index: 1;
      border-radius: ${pxToRem(12)};
    }
    
    ${props => props.$active && '&:before { display: none; }'}

    ${props => props.$active && `
      box-shadow: ${props.theme.palette.custom.pink};
      box-shadow: 0 ${props.theme.typography.pxToRem(4)} ${props.theme.typography.pxToRem(6)} ${props.theme.palette.custom.pink};
    `}
  }
`